/**
 * API Client Wrapper
 * Centralized HTTP client for backend API communication
 */

import { ApiError } from "../models/api"
import i18n from "../assets/translations/i18n"

const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:4000"

let authRedirectInProgress = false

function mergeHeaders(
    base: Record<string, string>,
    extra?: HeadersInit
): Record<string, string> {
    if (!extra) return base

    if (extra instanceof Headers) {
        extra.forEach((value: string, key: string) => {
            base[key] = value
        })
        return base
    }

    if (Array.isArray(extra)) {
        extra.forEach(([key, value]) => {
            base[key] = value
        })
        return base
    }

    return { ...base, ...(extra as Record<string, string>) }
}

/**
 * Get auth token from localStorage
 */
function getToken() {
    return localStorage.getItem("accessToken")
}

/**
 * Set auth token in localStorage
 */
export function setToken(token: string) {
    localStorage.setItem("accessToken", token)
}

/**
 * Remove auth token from localStorage
 */
export function removeToken() {
    localStorage.removeItem("accessToken")
}

function shouldForceLogout(statusCode: number, message?: string) {
    if (statusCode !== 401) return false
    return (message || "").toLowerCase().includes("access token expired")
}

function mapDomainErrorMessage(
    domainCode?: string,
    fallbackMessage?: string
): string {
    switch (domainCode) {
        case "POS_AUTH_FORBIDDEN":
            return "Accès refusé pour cette opération POS."
        case "POS_AUTH_REQUIRED":
            return "Votre session a expiré. Veuillez vous reconnecter."
        case "POS_REGISTER_OCCUPIED":
            return "Ce poste de caisse est déjà utilisé."
        case "POS_SALE_NOT_OPEN":
            return "Le ticket n'est plus en état modifiable. Rechargez la vente puis réessayez."
        case "POS_CART_LOCKED":
            return "Panier verrouillé après un premier règlement."
        case "POS_OVERPAYMENT_BLOCKED":
            return "Le montant dépasse le restant dû pour ce mode de règlement."
        case "POS_SESSION_AUTO_CLOSED":
            return "Votre session de caisse a été automatiquement clôturée pour inactivité."
        case "POS_VALIDATION":
            return fallbackMessage || "Veuillez corriger les données saisies."
        default:
            return fallbackMessage || (i18n.t("common.genericError") as string)
    }
}

export function handleUnauthorizedResponse(
    statusCode: number,
    message?: string
) {
    if (!shouldForceLogout(statusCode, message)) return

    removeToken()
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("cashierFlow")

    if (typeof window === "undefined") return
    if (window.location.hash.startsWith("#/login")) return
    if (authRedirectInProgress) return

    sessionStorage.setItem("sessionExpired", "1")
    authRedirectInProgress = true
    window.location.replace("/#/login")
}

/**
 * Make HTTP request to backend API
 * @param {string} endpoint - API endpoint (e.g., '/api/auth/login')
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
async function request(endpoint: string, options: RequestInit = {}) {
    const url = `${API_BASE_URL}${endpoint}`

    const token = getToken()
    const headers = mergeHeaders(
        {
            "Content-Type": "application/json",
        },
        options.headers
    )

    if (token) {
        headers.Authorization = `Bearer ${token}`
    }

    const config: RequestInit = {
        ...options,
        headers,
    }

    try {
        const response = await fetch(url, config)

        const data = await response.json().catch(() => null)

        if (!response.ok) {
            const err = new Error(
                data.error?.message || "Request failed"
            ) as ApiError
            err.statusCode = response.status
            err.code = data.error?.code
            err.domainCode = data.error?.domainCode
            err.details = data.error?.details
            err.userMessage = mapDomainErrorMessage(
                data.error?.domainCode,
                data.error?.message
            )
            err.message = err.userMessage
            handleUnauthorizedResponse(response.status, data?.error?.message)
            throw err
        }

        return data
    } catch (error: unknown) {
        const err = error as ApiError
        if (err?.statusCode) {
            throw err
        }
        throw new Error(`Network error: ${err?.message || "Unknown error"}`)
    }
}

/**
 * HTTP GET request
 */
export async function get(endpoint: string, options: RequestInit = {}) {
    return request(endpoint, {
        ...options,
        method: "GET",
    })
}

/**
 * HTTP POST request
 */
export async function post(
    endpoint: string,
    body?: unknown,
    options: RequestInit = {}
) {
    const config: RequestInit = {
        ...options,
        method: "POST",
    }
    if (body !== undefined) {
        config.body = JSON.stringify(body)
    }
    return request(endpoint, config)
}

/**
 * HTTP PUT request
 */
export async function put(
    endpoint: string,
    body?: unknown,
    options: RequestInit = {}
) {
    const config: RequestInit = {
        ...options,
        method: "PUT",
    }
    if (body !== undefined) {
        config.body = JSON.stringify(body)
    }
    return request(endpoint, config)
}

/**
 * HTTP PATCH request
 */
export async function patch(
    endpoint: string,
    body?: unknown,
    options: RequestInit = {}
) {
    const config: RequestInit = {
        ...options,
        method: "PATCH",
    }
    if (body !== undefined) {
        config.body = JSON.stringify(body)
    }
    return request(endpoint, config)
}

/**
 * HTTP DELETE request
 */
export async function del(endpoint: string, options: RequestInit = {}) {
    return request(endpoint, {
        ...options,
        method: "DELETE",
    })
}

const apiClient = {
    get,
    post,
    put,
    patch,
    delete: del,
    setToken,
    removeToken,
}

export default apiClient
